import {Modal, notification} from 'antd';
import {useResetUserPasswordMutation, UserFieldsFragment} from 'api/generated';
import {userQuery} from 'api/queries';
import {Formik} from 'formik';
import {Form, FormItem, Input} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import React from 'react';
import * as yup from 'yup';

type Props = {
  user?: UserFieldsFragment | null;
  isOpen: boolean;
  onClose: () => void;
};

const validationSchema = yup.object().shape({
  userId: yup.string().required(),
  temporalPassword: yup.string()
});

export const ResetPassword: React.FC<Props> = ({user, isOpen, onClose}) => {
  const [resetPassword] = useResetUserPasswordMutation();
  if (!user) return null;

  const initialValues = {
    userId: user.id
  };

  const handleSubmit = async (
    values: typeof initialValues,
    {setSubmitting, resetForm}: FormikHelpers<typeof initialValues>
  ) => {
    try {
      await resetPassword({
        variables: values,
        refetchQueries: [{query: userQuery, variables: {id: user.id}}]
      });
      notification.info({
        message: 'Password has been reset successfully',
        description: `The user will receive an email with a new password on ${user.email}`
      });
      onClose();
      setTimeout(() => {
        resetForm();
      }, 250);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}>
      {({submitForm, isSubmitting, resetForm}) => (
        <Modal
          title="Reset user password"
          okText="Reset"
          open={isOpen}
          confirmLoading={isSubmitting}
          onOk={submitForm}
          onCancel={() => {
            onClose();
            setTimeout(resetForm, 100);
          }}>
          <Form layout="vertical">
            <p>
              The user will receive an email with a new password. They will be asked to change it on
              the next login.
            </p>
            <FormItem
              name="temporalPassword"
              label="Temporary password (optional)"
              extra="If this field is empty new password will be generated automatically">
              <Input name="temporalPassword" />
            </FormItem>
            <input type="submit" value="submit" hidden />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
